/**
 * Send Email Dialog for generated documents.
 * Opens after a document is generated; fetches suggested subject/body from the AI.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, Paperclip, FileText } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  generatedId: string | null;
  filename?: string;
  defaultTo?: string;
}

export function SendEmailDialog({ open, onOpenChange, generatedId, filename, defaultTo }: SendEmailDialogProps) {
  const [to, setTo] = useState(defaultTo || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (open) {
      setTo(defaultTo || '');
      setCc('');
      setSubject('');
      setBody('');
      setPrefilled(false);
    }
  }, [open, defaultTo]);

  const { data: suggested, isLoading: suggestLoading } = useQuery({
    queryKey: ['automation', 'suggest-email', generatedId],
    queryFn: async () => {
      if (!generatedId) return null;
      const resp = await apiClient.get(`/automation/generated/${generatedId}/suggest-email`);
      return resp.data?.data || resp.data;
    },
    enabled: !!generatedId && open,
  });

  useEffect(() => {
    if (suggested && !prefilled) {
      if (suggested.subject) setSubject(suggested.subject);
      if (suggested.body) setBody(suggested.body);
      setPrefilled(true);
    }
  }, [suggested, prefilled]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!generatedId) throw new Error('No document');
      const resp = await apiClient.post(`/automation/generated/${generatedId}/send-email`, {
        toEmail: to,
        cc: cc || undefined,
        subject,
        body,
      });
      return resp.data?.data || resp.data;
    },
    onSuccess: () => {
      toast.success('Email sent to client.');
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to send email');
    },
  });

  const canSend = !!to.trim() && !!subject.trim() && !!body.trim() && !sendMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Send to Client</DialogTitle>
          <DialogDescription>Review and send the generated document.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email-to">To *</Label>
            <Input id="email-to" type="email" placeholder="client@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-cc">CC</Label>
            <Input id="email-cc" type="email" placeholder="Optional - comma separated" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject *</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={suggestLoading ? 'Generating suggestion…' : ''}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-body">Body *</Label>
            <Textarea
              id="email-body"
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={suggestLoading ? 'Generating suggestion…' : ''}
            />
          </div>

          {filename && (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2 text-xs">
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
              <FileText className="h-4 w-4 text-red-500 shrink-0" />
              <span className="flex-1 truncate">{filename}</span>
              <span className="text-muted-foreground">PDF - automatically attached</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sendMutation.isPending}>Cancel</Button>
          <Button onClick={() => sendMutation.mutate()} disabled={!canSend}>
            {sendMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SendEmailDialog;
